import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

import type { SharedProps } from "@/types"

type Props = {
  organisations: { id: number; name: string }[]
  teams: { id: number; name: string; organisation_id: number }[]
}

function UserNew({ organisations }: Props) {
  const { current_user } = usePage<SharedProps>().props

  const { data, setData, post, processing } = useForm({
    first_name: "",
    last_name: "",
    email_address: "",
    organisation_id: current_user?.is_super_admin ? "" : String(current_user?.organisation_id || ""),
    role: "user",
    is_active: true,
    phone_number: "",
    job_title: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/users")
  }

  return (
    <>
      <Head title="Nouvel utilisateur" />
      <PageHeader title="Nouvel utilisateur" back="/users" />
      <div className="space-y-6">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom</Label>
                  <Input
                    id="first_name"
                    value={data.first_name}
                    onChange={(e) => setData("first_name", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom</Label>
                  <Input
                    id="last_name"
                    value={data.last_name}
                    onChange={(e) => setData("last_name", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={data.email_address}
                  onChange={(e) => setData("email_address", e.target.value)}
                  required
                />
              </div>

              <p className="text-sm text-muted-foreground">
                Un email sera envoyé à l'utilisateur pour créer son mot de passe.
              </p>

              {current_user?.is_super_admin && (
                <div className="space-y-2">
                  <Label htmlFor="organisation">Organisation</Label>
                  <Select value={data.organisation_id} onValueChange={(v) => setData("organisation_id", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      {organisations.map((org) => (
                        <SelectItem key={org.id} value={String(org.id)}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="role">Rôle</Label>
                <Select value={data.role} onValueChange={(v) => setData("role", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utilisateur</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={data.phone_number}
                    onChange={(e) => setData("phone_number", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_title">Poste</Label>
                  <Input id="job_title" value={data.job_title} onChange={(e) => setData("job_title", e.target.value)} />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch id="is_active" checked={data.is_active} onCheckedChange={(v) => setData("is_active", v)} />
                <Label htmlFor="is_active">Actif</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" asChild>
                  <Link href="/users">Annuler</Link>
                </Button>
                <Button type="submit" disabled={processing}>
                  Créer
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </>
  )
}

UserNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default UserNew
